import cn from 'classnames'
import type { ReactNode } from 'react'

import styles from './CollapsibleIconButton.module.scss'

interface ICollapsibleIconButtonProps {
  /** Rendered as given, so an asset that needs its own fill or stroke handling keeps it. */
  icon: ReactNode
  label: ReactNode
  /** The button reads as its label alone while collapsed, so the accessible name is spelled out. */
  ariaLabel: string
  /** Holds the label open beyond hover and focus — a button whose popup is showing, say. Leave it
   *  out on a button that discloses nothing: it also decides whether `aria-expanded` is announced
   *  at all. */
  isOpen?: boolean
  disabled?: boolean
  className?: string
  /** Marks the button as a widget-tour target. Left out, the attribute is absent, so a tour step
   *  aimed at a button that is not on screen is skipped rather than landing on an empty box. */
  tourId?: string
  onClick?: () => void
}

/**
 * A 32px disc that widens to its label on hover and focus. Used for the one or two outward links
 * a dense control row can carry without outweighing the chips beside them.
 */
export const CollapsibleIconButton = ({
  icon,
  label,
  ariaLabel,
  isOpen,
  disabled = false,
  className,
  tourId,
  onClick,
}: ICollapsibleIconButtonProps) => (
  <button
    type="button"
    className={cn(styles.root, { [styles.opened]: isOpen }, className)}
    data-tour-id={tourId}
    aria-label={ariaLabel}
    aria-expanded={isOpen}
    disabled={disabled}
    onClick={onClick}
  >
    <span className={styles.iconSlot}>{icon}</span>
    <span className={styles.caption}>{label}</span>
  </button>
)

CollapsibleIconButton.displayName = 'CollapsibleIconButton'
